/**
 * Shared sticky-layout offsets for the PF revamp step shell.
 *
 * The step shell pins a top zone to the viewport top: the `PfTopBar` (height
 * `PF_TOPBAR_HEIGHT`) with the `PfProgressStepper` (height `PF_STEPPER_HEIGHT`)
 * stacked directly beneath it. Anything that pins below this zone — the
 * catalogue category tab strip, the desktop CartSidebar, and the catalogue
 * scroll-into-view anchor margin — must offset by the COMBINED height
 * (`PF_STICKY_TOP_OFFSET`) or it slides under the stepper.
 *
 * Keeping these values in one module is what stops the topbar/stepper heights
 * and their three downstream consumers from drifting apart (they used to each
 * hardcode `7.2rem`).
 */

/** Height of the sticky `PfTopBar` row (title + autosave + close). */
export const PF_TOPBAR_HEIGHT = '7.2rem'

/** Height of the `PfTopBar` row on mobile, tighter so the stepper sits close under the title. */
export const PF_TOPBAR_HEIGHT_MOBILE = '5.6rem'

/**
 * Height of the `PfProgressStepper` row pinned directly beneath the topbar in
 * the BAND variant. Fixed (its content is vertically centred) so the combined
 * offset below is exact on both mobile and desktop. Bumped from 4.8rem to give
 * the band more breathing room ("less compact" band variant).
 */
export const PF_STEPPER_HEIGHT = '6.4rem'

/** Height of the stepper row on mobile, where it is only the segment bar. */
export const PF_STEPPER_HEIGHT_MOBILE = '1.6rem'

/**
 * Combined sticky top offset for the BAND variant: topbar + stepper row. Also
 * the fallback for the `--pf-sticky-top` CSS var (see `PF_STICKY_TOP`) and the
 * value the desktop band layout pins the sticky zone to.
 */
export const PF_STICKY_TOP_OFFSET = `calc(${PF_TOPBAR_HEIGHT} + ${PF_STEPPER_HEIGHT})`

/** Combined sticky top offset on mobile: topbar + the slim stepper row. */
export const PF_STICKY_TOP_OFFSET_MOBILE = `calc(${PF_TOPBAR_HEIGHT_MOBILE} + ${PF_STEPPER_HEIGHT_MOBILE})`

/**
 * Sticky top offset for the INLINE variant — the stepper lives inside the
 * topbar row, so there's no second band and the sticky zone is just the topbar.
 */
export const PF_STICKY_TOP_OFFSET_TOPBAR = PF_TOPBAR_HEIGHT

/**
 * Sticky top offset inside the Propstack embed — the host page is the chrome,
 * so the sticky zone is the stepper band alone.
 */
export const PF_STICKY_TOP_OFFSET_EMBED = PF_STEPPER_HEIGHT

/**
 * Name of the CSS custom property the `StepShell` sets on its root to the
 * active variant's sticky-zone height (band vs. inline). Every sticky child
 * that pins below the top zone reads it through `PF_STICKY_TOP` so the offset
 * follows the variant without each consumer re-resolving the flag.
 */
export const PF_STICKY_TOP_VAR = '--pf-sticky-top'

/**
 * The value sticky children should use for their `top` / `calc()` offsets.
 * Resolves to the live `--pf-sticky-top` set by `StepShell`, falling back to
 * the band offset if the var is ever absent (keeps standalone usage safe).
 */
export const PF_STICKY_TOP = `var(${PF_STICKY_TOP_VAR}, ${PF_STICKY_TOP_OFFSET})`

/**
 * Height reserved for the fixed mobile bottom action bar (`MobileBottomBar`).
 * Doubles as the bottom padding the shell adds to its scroll content on mobile
 * so the last cards never hide behind the fixed bar.
 */
export const PF_MOBILE_BOTTOM_BAR_HEIGHT = '7.2rem'

/** Background extended sideways by the page gutter (rem, one value or per breakpoint). */
export const gutterBleed = (gutter: number | Record<string, number>, color: string) => {
  const bleed = (rem: number) => ({
    boxShadow: `0 0 0 ${rem}rem ${color}`,
    clipPath: `inset(0 -${rem}rem)`,
  })
  if (typeof gutter === 'number') return bleed(gutter)
  const entries = Object.entries(gutter).map(([breakpoint, rem]) => [breakpoint, bleed(rem)] as const)
  return {
    boxShadow: Object.fromEntries(entries.map(([breakpoint, v]) => [breakpoint, v.boxShadow])),
    clipPath: Object.fromEntries(entries.map(([breakpoint, v]) => [breakpoint, v.clipPath])),
  }
}
